<script lang="ts">
import type { Nullable, User } from '@/types'
import type { TIcon } from '@/utils/helper-objects'
import {
  NamedThemeColors,
  type TNamedThemeColorValue,
} from '@goodlifeplus/design-system/theme'
import { type PropType, defineComponent } from 'vue'

import DepositModal from '../modals/DepositModal.vue'

/**
 * SidebarBalanceCard
 */
export default defineComponent({
  components: {
    DepositModal,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      required: true,
    },
  },
  computed: {
    subscriptionIcon(): TIcon {
      switch (this.$userStore.highestPlanTier) {
        case 'standard':
          return this.Icons.Star
        case 'platinum':
          return this.Icons.Lightening
        case 'vip':
          return this.Icons.Crown
        default:
          return this.Icons.Star
      }
    },
    subscriptionColor(): TNamedThemeColorValue | undefined {
      switch (this.$userStore.highestPlanTier) {
        case 'standard':
          return NamedThemeColors.Neutral
        case 'platinum':
          return NamedThemeColors.Secondary
        case 'vip':
          return undefined // Gold gradient
        default:
          return NamedThemeColors.Neutral
      }
    },
  },
})
</script>

<template>
  <div
    v-if="$userStore.user"
    class="balance-card flex flex-col gap-3 rounded-xl p-3 border border-[#B6C6CF] bg-pure-white"
  >
    <div class="flex justify-between items-center self-stretch">
      <p class="font-bold text-xl">
        {{ user.first_name }}
        {{ user.last_name?.charAt(0).toLocaleUpperCase() }}.
      </p>

      <AppBadge
        v-if="
          $userStore.highestPlanTier &&
          $userStore.highestPlanTier?.toLowerCase() !== 'none'
        "
        :color="subscriptionColor"
        size="md"
        class="!rounded-md !px-2 !py-1 capitalize !leading-none flex gap-1"
        :class="
          subscriptionColor
            ? $userStore.highestPlanTier === 'standard'
              ? '!text-[#5F7D92]'
              : '!text-pure-white'
            : 'bg-gradient-gold'
        "
      >
        <AppIcon :icon="subscriptionIcon" size="xs" />
        {{ $t(`membership_badge.${$userStore.highestPlanTier}`) }}
      </AppBadge>
    </div>

    <div class="grid grid-cols-[1fr_auto] gap-x-4 gap-y-3">
      <span class="font-extrabold">Balance: </span>
      <strong class="text-right">{{
        $n(user.credit.totals.overall / 100, 'currency')
      }}</strong>
    </div>
    <div class="grid grid-cols-[1fr_auto] gap-x-4">
      <span class="font-extrabold italic"
        >LEVEL {{ user.user_progression.level ?? 0 }}</span
      >
      <strong class="text-right"
        >{{ $n(user.user_progression.xp_earned ?? 0, 'number') }} Points</strong
      >
    </div>

    <div class="flex flex-col gap-2">
      <AppButton
        class="button-secondary !w-full !py-2 !px-4 !text-sm !font-bold !leading-none"
        :route="{ name: 'your-level' }"
      >
        My Points Area
      </AppButton>
      <DepositModal>
        <template #button="{ openModal }">
          <AppButton
            class="button-primary !w-full !py-2 !px-4 !text-sm !font-bold !leading-none"
            @click="openModal"
          >
            Deposit Now
          </AppButton>
        </template>
      </DepositModal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dp-container {
  @apply relative flex items-center justify-center p-3 italic;

  clip-path: circle(closest-side at 50%);
  background-color: #fff;
  background-size: 100% 100%;
  background-position:
    0px 0px,
    0px 0px,
    0px 0px;

  &::before {
    @apply absolute h-[85%] w-[85%] bg-black;

    content: '';
    display: block;
    clip-path: circle(closest-side at 50%);
  }
}

.property-list:deep(.contents) {
  span {
    @apply font-extrabold italic;

    &:nth-child(2) {
      @apply not-italic;
    }
  }
  @apply gap-y-3 leading-5;
}
</style>
